import { AppDrawer } from '@/components/commons/appDrawer/appDrawer'
import { Button, Typography, Space, App, Flex } from 'antd'
import {
  CaretUpOutlined,
  CaretDownOutlined,
  HolderOutlined,
} from '@ant-design/icons'
import { useEffect, useState } from 'react'
import { Category } from '@/types/category'
import { useUpdateCategorySequence } from '@/hooks/category/useUpdateCategory'
import { getBranchFromPathUrl } from '@/utils/helpers/urlHelper'
import { queryClient } from '@/configs/tanstack'
import { QUERY_KEY_CATEGORY } from '@/hooks/category/useGetCategories'
import { useTranslation } from 'react-i18next'

const { Text } = Typography

interface ICategoryOrderDrawer {
  open: boolean
  onCancel: () => void
  categories: Category[]
}

export const CategoryOrderDrawer = ({
  open,
  onCancel,
  categories,
}: ICategoryOrderDrawer) => {
  const { t } = useTranslation('catalog')
  const [ordered, setOrdered] = useState<Category[]>([])
  const { mutate, isPending } = useUpdateCategorySequence()
  const branch = getBranchFromPathUrl()
  const { message } = App.useApp()

  useEffect(() => {
    if (open && categories.length) {
      setOrdered([...categories])
    }
  }, [open, categories])

  const moveUp = (index: number) => {
    if (index === 0) return
    const newOrder = [...ordered]
    ;[newOrder[index - 1], newOrder[index]] = [
      newOrder[index],
      newOrder[index - 1],
    ]
    setOrdered(newOrder)
  }

  const moveDown = (index: number) => {
    if (index === ordered.length - 1) return
    const newOrder = [...ordered]
    ;[newOrder[index], newOrder[index + 1]] = [
      newOrder[index + 1],
      newOrder[index],
    ]
    setOrdered(newOrder)
  }

  const handleSave = () => {
    const payload = ordered.map((el, i) => ({
      id: el.id,
      sequence: i,
    }))
    mutate(
      { branch, payload },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEY_CATEGORY, branch],
          })
          message.success(t('catalog.category.order.saved'))
          onCancel()
        },
      },
    )
  }

  return (
    <AppDrawer
      title={t('catalog.category.order.title')}
      open={open}
      onClose={onCancel}
      size={480}
      footer={
        <Button block type="primary" onClick={handleSave} loading={isPending}>
          {t('common.actions.save')}
        </Button>
      }
    >
      <Text type="secondary" className="block mb-4">
        {t('catalog.category.order.desc')}
      </Text>

      <div className="flex flex-col gap-1 max-h-[calc(100vh-200px)] overflow-y-auto">
        {ordered.map((item, index) => {
          const isFirst = index === 0
          const isLast = index === ordered.length - 1

          return (
            <Flex
              key={item.id}
              align="center"
              gap={12}
              className="px-3 py-2.5 hover:bg-gray-50 rounded-lg transition-colors border border-transparent hover:border-gray-100"
            >
              <HolderOutlined className="flex-shrink-0 text-sm text-gray-300" />
              <Text
                type="secondary"
                className="flex-shrink-0 text-xs min-w-[22px] text-center"
              >
                {index + 1}
              </Text>
              <div
                className="flex-shrink-0 w-3 h-3 rounded-full"
                style={{ background: item.color }}
              />
              <div className="flex-1 min-w-0">
                <Text className="block font-medium truncate">{item.name}</Text>
                {item.description && (
                  <Text type="secondary" className="block text-xs truncate">
                    {item.description}
                  </Text>
                )}
              </div>
              <Space.Compact>
                <Button
                  icon={<CaretUpOutlined />}
                  disabled={isFirst}
                  onClick={() => moveUp(index)}
                  size="small"
                />
                <Button
                  icon={<CaretDownOutlined />}
                  disabled={isLast}
                  onClick={() => moveDown(index)}
                  size="small"
                />
              </Space.Compact>
            </Flex>
          )
        })}
      </div>
    </AppDrawer>
  )
}
